import {
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useRtl } from "@ryde/redux";
import { hasKeys, type ColumnSizingState } from "@ryde/shared";

import type {
  IColumnResizeDrag,
  IColumnsSizingStore,
} from "./column-resize.models";
import {
  getKeyboardResizedColumnWidth,
  getPointerResizedColumnWidth,
  measureColumnLineHeight,
  measureHeaderRowColumnsSizing,
} from "./column-resize.utils";

interface IUseColumnResizeParams {
  columnId: string;
  columnLabel?: string | null;
  columnWidth?: string | number;
  columnsSizingStore: IColumnsSizingStore;
}

export const useColumnResize = ({
  columnId,
  columnLabel,
  columnWidth,
  columnsSizingStore,
}: IUseColumnResizeParams) => {
  const { t } = useTranslation();
  const { isRtl } = useRtl();
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef<IColumnResizeDrag | null>(null);
  const headerCellRef = useRef<HTMLTableCellElement | null>(null);

  // A drag writes the width onto the header cell directly so no row re-renders while the pointer moves; once
  // the committed width renders, the cell's own style takes over again.
  useLayoutEffect(() => {
    if (!dragRef.current && headerCellRef.current) {
      headerCellRef.current.style.width = "";
    }
  }, [columnWidth]);

  // Every column gets its current width the first time one is resized, so the table stops stretching and
  // the drag moves only the dragged column.
  const getFrozenColumnsSizing = (
    headerCell: HTMLTableCellElement,
  ): ColumnSizingState => {
    const columnsSizing = columnsSizingStore.getColumnsSizing();

    return hasKeys(columnsSizing)
      ? columnsSizing
      : measureHeaderRowColumnsSizing(headerCell);
  };

  const onPointerDown = useEvent((event: PointerEvent<HTMLSpanElement>) => {
    const headerCell = event.currentTarget.closest("th");

    if (event.button !== 0 || !headerCell) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const isLayoutFrozenByDrag = !hasKeys(
      columnsSizingStore.getColumnsSizing(),
    );

    if (isLayoutFrozenByDrag) {
      columnsSizingStore.setColumnsSizing(getFrozenColumnsSizing(headerCell));
    }

    event.currentTarget.style.setProperty(
      "--column-resize-line-height",
      `${measureColumnLineHeight(headerCell)}px`,
    );
    event.currentTarget.setPointerCapture(event.pointerId);
    headerCellRef.current = headerCell;
    dragRef.current = {
      headerCell,
      startClientX: event.clientX,
      startWidth: headerCell.getBoundingClientRect().width,
      isLayoutFrozenByDrag,
    };
    setIsResizing(true);
  });

  const onPointerMove = useEvent((event: PointerEvent<HTMLSpanElement>) => {
    const drag = dragRef.current;

    if (!drag) {
      return;
    }

    drag.width = getPointerResizedColumnWidth({
      startWidth: drag.startWidth,
      startClientX: drag.startClientX,
      clientX: event.clientX,
      isRtl,
    });
    drag.headerCell.style.width = `${drag.width}px`;
  });

  // Runs for pointerup, pointercancel and lostpointercapture alike; the first one to arrive ends the drag.
  const onPointerEnd = useEvent(() => {
    const drag = dragRef.current;

    if (!drag) {
      return;
    }

    dragRef.current = null;
    setIsResizing(false);

    if (drag.width === undefined) {
      if (drag.isLayoutFrozenByDrag) {
        columnsSizingStore.setColumnsSizing({});
      }

      return;
    }

    columnsSizingStore.commitColumnsSizing({
      ...columnsSizingStore.getColumnsSizing(),
      [columnId]: drag.width,
    });
  });

  const onKeyDown = useEvent((event: KeyboardEvent<HTMLSpanElement>) => {
    const headerCell = event.currentTarget.closest("th");

    if (!headerCell) {
      return;
    }

    const width = getKeyboardResizedColumnWidth({
      width: headerCell.getBoundingClientRect().width,
      key: event.key,
      isRtl,
    });

    if (width === undefined) {
      return;
    }

    event.preventDefault();
    headerCellRef.current = headerCell;
    columnsSizingStore.commitColumnsSizing({
      ...getFrozenColumnsSizing(headerCell),
      [columnId]: width,
    });
  });

  return {
    isResizing,
    ariaLabel: t("general.RESIZE_COLUMN", { column: columnLabel || columnId }),
    ariaValueNow: typeof columnWidth === "number" ? columnWidth : undefined,
    onPointerDown,
    onPointerMove,
    onPointerEnd,
    onKeyDown,
  };
};
